import {
  calendarDateIn,
  DECISION_OTHER_KEY,
  type DecisionGroupView,
  type DecisionQuestionView,
  type DecisionSheetView,
  isRecommendedAnswer,
  minutesOfDayIn,
  splitDecisionKeys,
} from '@apm/contracts';
import { displayDate, displayDayMonth } from '../../lib/date';
import { orgTimezone } from '../../lib/org-timezone';

/**
 * Phần thuần của trang sổ — đếm, lọc, gom nhóm, văn bản kết quả. Tách khỏi
 * component để test được không cần dựng React (APM-DSHEET-c "Xong khi").
 */

/** `link` — câu do người ngoài chốt qua link công khai (APM-DSX); chỉ hiện khi có. */
export type DecisionFilter = 'all' | 'open' | 'answered' | 'link';
export const DECISION_FILTERS: readonly DecisionFilter[] = ['all', 'open', 'answered'];

export interface DecisionTotals {
  readonly total: number;
  readonly answered: number;
  /** Đáp án trùng phương án "nên chọn" — ô "Chọn đúng đề xuất". */
  readonly recommended: number;
  readonly pending: number;
}

export function decisionTotals(questions: readonly DecisionQuestionView[]): DecisionTotals {
  let answered = 0;
  let recommended = 0;
  for (const question of questions) {
    if (!question.answer) continue;
    answered += 1;
    if (isRecommendedAnswer(question, question.answer.key)) recommended += 1;
  }
  return { total: questions.length, answered, recommended, pending: questions.length - answered };
}

export function matchesFilter(question: DecisionQuestionView, filter: DecisionFilter): boolean {
  if (filter === 'open') return question.answer === null;
  if (filter === 'answered') return question.answer !== null;
  if (filter === 'link') return Boolean(question.answer?.respondent);
  return true;
}

export interface LinkRespondent {
  readonly name: string;
  readonly email: string;
  readonly verified: boolean;
  /** Số câu người này đang là người chốt (đáp án hiện tại). */
  readonly count: number;
}

/**
 * Người trả lời qua link công khai, gộp theo email — cho dòng "Trả lời qua
 * link" dưới "Người chốt". Họ không nằm trong danh sách người chốt (ai có link
 * "Trả lời" đều chốt được), nên không có dòng này thì đầu sổ không cho thấy có
 * người ngoài đã chốt (người dùng chọn 2026-10-07). Nhiều câu trước.
 */
export function linkRespondents(questions: readonly DecisionQuestionView[]): LinkRespondent[] {
  const byEmail = new Map<string, LinkRespondent>();
  for (const question of questions) {
    const respondent = question.answer?.respondent;
    if (!respondent) continue;
    const key = respondent.email.toLowerCase() || respondent.name;
    const seen = byEmail.get(key);
    byEmail.set(key, {
      name: respondent.name || respondent.email,
      email: respondent.email,
      verified: respondent.verified,
      count: (seen?.count ?? 0) + 1,
    });
  }
  return [...byEmail.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

export interface DecisionSection {
  /** `null` = câu không thuộc nhóm nào — đứng cuối sổ. */
  readonly group: DecisionGroupView | null;
  /** Câu **sau** khi lọc, theo `position`. */
  readonly questions: readonly DecisionQuestionView[];
  /** Đếm trên **mọi** câu của nhóm, không theo bộ lọc: "3/5 đã chốt" là sự thật của nhóm. */
  readonly answered: number;
  readonly total: number;
}

/**
 * Gom câu theo nhóm, nhóm theo `position`.
 *
 * `keepEmpty` giữ nhóm không còn câu nào sau lọc — trình soạn cần thấy nhóm
 * rỗng để thêm câu vào; trang chốt thì không (một tiêu đề nhóm treo không có
 * câu nào dưới nó khi lọc "Chưa chốt" là nhiễu).
 */
export function decisionSections(
  view: Pick<DecisionSheetView, 'groups' | 'questions'>,
  filter: DecisionFilter,
  keepEmpty = false,
): DecisionSection[] {
  const byPosition = <T extends { position: number }>(a: T, b: T): number =>
    a.position - b.position;
  const groups = [...view.groups].sort(byPosition);
  const known = new Set(groups.map((group) => group.id));
  const questions = [...view.questions].sort(byPosition);

  const section = (
    group: DecisionGroupView | null,
    all: readonly DecisionQuestionView[],
  ): DecisionSection => ({
    group,
    questions: all.filter((question) => matchesFilter(question, filter)),
    answered: all.filter((question) => question.answer !== null).length,
    total: all.length,
  });

  const sections = groups.map((group) =>
    section(
      group,
      questions.filter((question) => question.groupId === group.id),
    ),
  );
  const loose = questions.filter(
    (question) => question.groupId === null || !known.has(question.groupId),
  );
  if (loose.length > 0) sections.push(section(null, loose));

  return keepEmpty ? sections : sections.filter((entry) => entry.questions.length > 0);
}

/** "Phương án khác" mà chưa có ghi chú — server sẽ trả 400, nên giao diện chặn trước. */
export function noteRequired(key: string | null, note: string): boolean {
  return splitDecisionKeys(key).includes(DECISION_OTHER_KEY) && note.trim() === '';
}

/**
 * Tên phương án theo mã; `X` là "Phương án khác". Đáp án của câu chọn nhiều
 * (`"A,C"`) ⇒ các tên nối bằng "; " — mọi chỗ đang ghép `${key}. ${title}`
 * (lịch sử, RAID, xuất tài liệu, kết quả chép) đọc ra "A,C. Email; SMS".
 */
export function optionTitle(
  question: Pick<DecisionQuestionView, 'options'>,
  key: string,
  otherLabel: string,
): string {
  return splitDecisionKeys(key)
    .map((one) =>
      one === DECISION_OTHER_KEY
        ? otherLabel
        : (question.options.find((option) => option.key === one)?.title ?? one),
    )
    .join('; ');
}

const pad = (value: number): string => String(value).padStart(2, '0');

function clock(at: Date, timezone: string): string {
  const minutes = minutesOfDayIn(at, timezone);
  return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
}

/**
 * `dd/MM HH:mm` theo **múi giờ tổ chức** — dấu vết "Chọn D · 07/10 14:32 · tên".
 * Không `toISOString().slice` (bẫy ngày UTC): cùng một lần chốt hiện cùng một
 * giờ với cả đội, không theo đồng hồ của máy đang xem.
 */
export function stampDayTime(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  const timezone = orgTimezone();
  return `${displayDayMonth(calendarDateIn(at, timezone))} ${clock(at, timezone)}`;
}

/** `HH:mm` theo múi giờ tổ chức — toast xung đột "… lúc 14:32". */
export function stampTime(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? '—' : clock(at, orgTimezone());
}

/** `dd/MM/yyyy HH:mm` theo múi giờ tổ chức. */
export function stampDateTime(iso: string): string {
  return stampDateTimeIn(iso, orgTimezone());
}

/** `dd/MM/yyyy HH:mm` in an explicit timezone — for pure builders that take `tz` (export, RAID). */
export function stampDateTimeIn(at: string | Date, timezone: string): string {
  const date = typeof at === 'string' ? new Date(at) : at;
  if (Number.isNaN(date.getTime())) return '—';
  return `${displayDate(calendarDateIn(date, timezone))} ${clock(date, timezone)}`;
}

export interface ResultTextLabels {
  readonly other: string;
  readonly matchesRecommended: string;
  readonly unanswered: string;
  readonly note: string;
  readonly ungrouped: string;
}

/**
 * "Kết quả đã chốt" — văn bản thuần theo nhóm, cùng dạng trang mẫu (plan §2):
 *
 * ```
 * I. Tạm dừng, dừng hẳn…
 * - A-01 Tạm dừng chiến dịch đang chạy: D. Kết hợp A + B (đúng đề xuất)
 *   Ghi chú: …
 * - A-02 …: CHƯA CHỐT
 * ```
 */
export function decisionResultText(
  view: Pick<DecisionSheetView, 'title' | 'groups' | 'questions'>,
  labels: ResultTextLabels,
): string {
  const lines: string[] = [view.title];
  for (const section of decisionSections(view, 'all')) {
    lines.push('');
    lines.push(section.group ? `${section.group.code}. ${section.group.title}` : labels.ungrouped);
    for (const question of section.questions) {
      const head = `- ${question.code} ${question.title}`;
      if (!question.answer) {
        lines.push(`${head}: ${labels.unanswered}`);
        continue;
      }
      const { key, note } = question.answer;
      const suffix = isRecommendedAnswer(question, key) ? ` (${labels.matchesRecommended})` : '';
      lines.push(`${head}: ${key}. ${optionTitle(question, key, labels.other)}${suffix}`);
      if (note.trim()) lines.push(`  ${labels.note} ${note.trim()}`);
    }
  }
  return lines.join('\n');
}
